import { Injectable } from '@angular/core';
import { AuthenticationService } from '../../_core/_services/authentication.service';
import { UserService } from './user.service';
import { AuthorizationService } from 'src/app/_core/_services/authorization.service';
import { ERoles } from '../_models/entities/roles';

@Injectable({
  providedIn: 'root'
})
export class SystemUserService {
  constructor(
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private authz: AuthorizationService
  ) {}

  /**
   * Check if the current user is a system (back-office) user with appropriate
   * access, as opposed to a customer-portal user.
   * @returns true if user is authenticated and is not a portal customer
   */
  isSystemUser(): boolean {
    // First check if user is authenticated
    const authenticatedUser = this.authenticationService.userValue;
    if (!authenticatedUser || !authenticatedUser.access_token) {
      console.log('🔒 SystemUser: No authenticated user found');
      return false;
    }

    // Check if we're in the portal module by looking at the current URL
    const currentUrl = window.location.pathname;
    if (currentUrl.includes('/portal')) {
      console.log('🔒 SystemUser: Portal user detected, denying access');
      return false;
    }

    // Roles are fully backend-driven (created/assigned via System > Roles,
    // not a fixed frontend list - see ERoles's own limited, legacy usage
    // elsewhere in this file), so this can't be a hardcoded role-name
    // allow-list without needing a code change for every new role. Any role
    // at all is sufficient: UserService.currentUser is only ever populated
    // by the back-office /users/me endpoint (portal customers authenticate
    // through a completely separate PortalAuthenticationService/user model
    // and never reach this service), so a non-empty role set here already
    // implies "authenticated back-office user", regardless of which role.
    const userRoleNames = this.authz.getCurrentRoleNames();
    if (this.userService.currentUser && userRoleNames.length > 0) {
      console.log(`🔐 SystemUser: System user with role(s) '${userRoleNames}' granted access`);
      return true;
    }

    // If we can't determine the role yet, check if we have a valid JWT
    // token. This is a fallback for cases where AuthorizationService hasn't
    // finished loading roles/permissions yet - WebSocketService's
    // constructor in particular calls isSystemUser() synchronously at app
    // bootstrap, often before that load completes.
    if (authenticatedUser.access_token) {
      console.log('🔐 SystemUser: Authenticated user with valid token granted temporary access');
      return true;
    }

    console.log('🔒 SystemUser: No valid authentication or role found');
    return false;
  }

  /**
   * @deprecated returns one of the current user's role names (order not
   * guaranteed under multi-role). Prefer `getCurrentUserRoles`/`hasAnyRole`.
   */
  getCurrentUserRole(): string | null {
    return this.getCurrentUserRoles()[0] || null;
  }

  /**
   * Get all of the current user's role names.
   */
  getCurrentUserRoles(): string[] {
    const currentUser: any = this.userService.currentUser;
    if (Array.isArray(currentUser?.roles) && currentUser.roles.length > 0) {
      return currentUser.roles.map((r: any) => r.name).filter(Boolean);
    }
    return currentUser?.role?.name ? [currentUser.role.name] : [];
  }

  /**
   * Check if the current user has a specific role
   * @param role the role to check for
   * @returns true if user has the specified role
   */
  hasRole(role: ERoles): boolean {
    return this.getCurrentUserRoles().includes(role);
  }

  /**
   * Check if the current user has any of the specified roles
   * @param roles array of roles to check for
   * @returns true if user has any of the specified roles
   */
  hasAnyRole(roles: ERoles[]): boolean {
    const current = this.getCurrentUserRoles();
    return current.some((r) => roles.includes(r as ERoles));
  }
}
